import React, { useState } from "react";
import { X, Check, XCircle } from "lucide-react";

const ApprovalActions = ({ 
  payrollId, 
  onApprove, 
  onReject, 
  loading = false,
  showCancelApproval = false,
  onCancelApproval 
}) => {
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [approveRemarks, setApproveRemarks] = useState("");
  const [rejectRemarks, setRejectRemarks] = useState("");
  const [cancelRemarks, setCancelRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleApprove = async () => {
    if (!approveRemarks.trim()) {
      alert("Please enter remarks");
      return;
    }
    setSubmitting(true);
    try {
      await onApprove(payrollId, { remarks: approveRemarks });
      setShowApproveModal(false);
      setApproveRemarks("");
    } catch (error) {
      console.error("Error approving payroll:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectRemarks.trim()) {
      alert("Please enter reason for rejection");
      return;
    }
    setSubmitting(true);
    try {
      await onReject(payrollId, { remarks: rejectRemarks });
      setShowRejectModal(false);
      setRejectRemarks("");
    } catch (error) {
      console.error("Error rejecting payroll:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelApproval = async () => {
    if (!cancelRemarks.trim()) {
      alert("Please enter reason for cancellation");
      return;
    }
    setSubmitting(true);
    try {
      await onCancelApproval(payrollId, { remarks: cancelRemarks });
      setShowCancelModal(false);
      setCancelRemarks("");
    } catch (error) {
      console.error("Error cancelling approval:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2 font-poppins">
        <button
          onClick={() => setShowApproveModal(true)}
          disabled={loading}
          className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Check size={16} />
          Approve
        </button>
        <button
          onClick={() => setShowRejectModal(true)}
          disabled={loading}
          className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <XCircle size={16} />
          Reject
        </button>
        {showCancelApproval && (
          <button
            onClick={() => setShowCancelModal(true)}
            disabled={loading}
            className="flex items-center gap-1 px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X size={16} />
            Cancel Approval
          </button>
        )}
      </div>

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white font-poppins">
                Approve Payroll
              </h3>
              <button
                onClick={() => setShowApproveModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 font-poppins">
                Approval Remarks *
              </label>
              <textarea
                value={approveRemarks}
                onChange={(e) => setApproveRemarks(e.target.value)}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white font-poppins"
                placeholder="Enter approval remarks..."
              />
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowApproveModal(false)}
                disabled={submitting}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 font-medium font-poppins"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={submitting}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium font-poppins disabled:opacity-50"
              >
                {submitting ? "Approving..." : "Approve"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white font-poppins">
                Reject Payroll
              </h3>
              <button
                onClick={() => setShowRejectModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 font-poppins">
                Rejection Reason *
              </label>
              <textarea
                value={rejectRemarks}
                onChange={(e) => setRejectRemarks(e.target.value)}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white font-poppins"
                placeholder="Enter reason for rejection..."
              />
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowRejectModal(false)}
                disabled={submitting}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 font-medium font-poppins"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={submitting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md font-medium font-poppins disabled:opacity-50"
              >
                {submitting ? "Rejecting..." : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Approval Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white font-poppins">
                Cancel Approval
              </h3>
              <button
                onClick={() => setShowCancelModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 font-poppins">
                Cancellation Reason *
              </label>
              <textarea
                value={cancelRemarks}
                onChange={(e) => setCancelRemarks(e.target.value)}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 dark:bg-gray-700 dark:text-white font-poppins"
                placeholder="Enter reason for cancellation..."
              />
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowCancelModal(false)}
                disabled={submitting}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 font-medium font-poppins"
              >
                Close
              </button>
              <button
                onClick={handleCancelApproval}
                disabled={submitting}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md font-medium font-poppins disabled:opacity-50"
              >
                {submitting ? "Cancelling..." : "Cancel Approval"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ApprovalActions;

